import React, { useState } from "react";
import { motion } from "framer-motion";
import { Cpu, Sparkles, Lightbulb, ShieldAlert, Flag, Radar, Printer } from "lucide-react";
import { postAiBrief } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Overline, Badge, Loader } from "../components/Primitives";
import { ratingColor, POS } from "../lib/format";

const PROVIDERS = [
  { id: "claude", label: "Claude", model: "Sonnet 5.5" },
  { id: "gpt", label: "GPT", model: "5.6 Terra" },
  { id: "gemini", label: "Gemini", model: "3.1 Pro" },
];

const regimeColor = (r) => {
  if (/Bullish/i.test(r)) return POS;
  if (/Cautious/i.test(r)) return "#00e5ff";
  if (/Stagflation|Defensive/i.test(r)) return "#ffb300";
  return "#ff3b30";
};

export default function AIBriefing() {
  const [provider, setProvider] = useState("claude");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const generate = async () => {
    setLoading(true); setErr(null);
    try { setRes(await postAiBrief({ provider })); }
    catch (e) { setErr(e?.response?.data?.detail || "Generation failed. Try another model."); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="print-header mb-4">
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Alpha-Orchestrator — Weekly AI Investment Briefing</h1>
        <div style={{ fontSize: 12, color: "#666" }}>{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · Built by Abhishek Singh</div>
      </div>
      <Card className="p-5" testid="ai-controls" data-noprint>
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-2"><Cpu className="w-5 h-5 text-[#00e5ff]" /><div>
            <div className="text-sm font-semibold text-[#f8fafc]">AI Reasoning & Synthesis Layer</div>
            <div className="text-xs text-[#64748b]">Structured thesis · risk · action mandate from your live factor scores</div>
          </div></div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg border border-[#2d3c59] overflow-hidden" data-testid="provider-select">
              {PROVIDERS.map((p) => (
                <button key={p.id} data-testid={`provider-${p.id}`} onClick={() => setProvider(p.id)}
                  className={`px-3 py-2 text-xs transition-colors ${provider === p.id ? "bg-[#1a2338] text-[#00e5ff]" : "text-[#64748b] hover:text-[#94a3b8]"}`}>
                  <div className="font-medium">{p.label}</div><div className="font-mono text-[10px] opacity-70">{p.model}</div>
                </button>
              ))}
            </div>
            {res && <button data-testid="print-report-btn" onClick={() => window.print()}
              className="flex items-center gap-2 rounded-lg border border-[#2d3c59] text-[#94a3b8] px-3 py-2.5 text-sm hover:text-[#f8fafc] hover:border-[#00e5ff]/40 transition-colors">
              <Printer className="w-4 h-4" /> Weekly PDF</button>}
            <button data-testid="generate-brief-btn" onClick={generate} disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-[#00e5ff] text-[#06121a] font-medium px-4 py-2.5 hover:bg-[#33eaff] transition-colors disabled:opacity-50">
              <Sparkles className="w-4 h-4" /> {loading ? "Synthesising…" : "Generate Briefing"}
            </button>
          </div>
        </div>
      </Card>

      {err && <Card className="p-4 text-sm text-[#ff3b30]" testid="ai-error">{err}</Card>}
      {loading && <Loader label="AI is reasoning over factor scores, drift & macro…" />}

      {!loading && !res && !err && (
        <Card className="p-12 text-center" testid="ai-empty">
          <Radar className="w-8 h-8 text-[#2d3c59] mx-auto mb-3" />
          <div className="text-sm text-[#94a3b8]">Select a model and generate your daily alpha briefing.</div>
          <div className="text-xs text-[#64748b] mt-1">Each briefing cites the actual composite scores, valuation & momentum of your top-ranked assets.</div>
        </Card>
      )}

      {!loading && res && (
        <>
          {res.regime && (
            <Card glow className="p-5" testid="regime-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2"><Radar className="w-4 h-4 text-[#a855f7]" /><Overline>Macro Regime Classifier</Overline></div>
                <span className="font-mono text-[11px] text-[#64748b]">via {res.provider} · {new Date(res.generated_at).toLocaleTimeString()}</span>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <span className="text-2xl font-semibold tracking-tight" style={{ color: regimeColor(res.regime.regime) }}>{res.regime.regime}</span>
                <Badge color={regimeColor(res.regime.regime)}>{res.regime.confidence}% confidence</Badge>
              </div>
              <p className="text-sm text-[#94a3b8] mt-2 leading-relaxed max-w-3xl">{res.regime.summary}</p>
              {Array.isArray(res.regime.positioning) && (
                <div className="grid md:grid-cols-3 gap-3 mt-4">
                  {res.regime.positioning.map((pt, i) => (
                    <div key={i} className="rounded-lg border border-[#1e293b] p-3 text-xs text-[#94a3b8] leading-relaxed" data-testid={`positioning-${i}`}>{pt}</div>
                  ))}
                </div>
              )}
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {(res.briefings || []).map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
                <Card className="p-5 h-full" testid="ai-thesis-card">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-semibold text-[#f8fafc]">{b.name || b.symbol}</div>
                      <div className="font-mono text-[11px] text-[#64748b]">{b.symbol}</div>
                    </div>
                    <div className="text-right">
                      <Badge color={ratingColor(b.mandate)}>{b.mandate}</Badge>
                      {b.suggested_allocation_pct != null && <div className="font-mono text-xs text-[#94a3b8] mt-1.5">Alloc {b.suggested_allocation_pct}%</div>}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex gap-2.5">
                      <Lightbulb className="w-4 h-4 text-[#00e676] flex-shrink-0 mt-0.5" />
                      <div><div className="overline mb-0.5">Investment Thesis</div><p className="text-sm text-[#cbd5e1] leading-relaxed">{b.thesis}</p></div>
                    </div>
                    <div className="flex gap-2.5">
                      <ShieldAlert className="w-4 h-4 text-[#ffb300] flex-shrink-0 mt-0.5" />
                      <div><div className="overline mb-0.5">Key Risk Factor</div><p className="text-sm text-[#cbd5e1] leading-relaxed">{b.risk}</p></div>
                    </div>
                    <div className="flex gap-2.5">
                      <Flag className="w-4 h-4 text-[#00e5ff] flex-shrink-0 mt-0.5" />
                      <div><div className="overline mb-0.5">Action Mandate</div><p className="text-sm font-medium" style={{ color: ratingColor(b.mandate) }}>{b.mandate}{b.suggested_allocation_pct != null ? ` · target ${b.suggested_allocation_pct}% allocation` : ""}</p></div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
